import { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { Dropdown, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisV,
  faEye,
  faTimes,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Layout/DashboardLayout";

import CustomTable from "./../../Components/CustomTable";
import { placeholderImage } from "../../Assets/images";

import "./style.css";
import { questionModal, successModal } from "../../Components/CustomModal";
import CustomFilters from "../../Components/CustomFilters";
import BASEURL from "../../Config/global";
import CustomPagination from "../../Components/CustomPagination";
import CustomLoader from "../../Components/CustomLoader";
import { PermissionsContext } from "../../Layout/PermissionContext";
import { disabledButtonByPermission } from "../../Components/Permissions";
import { deleteRequest, postRequest } from "../../Util/requests";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { listSorted } from "../../Services/Sorting";
import { getQuestionModalTitleForSorting, sortingUpdatedSuccessfullyMsg } from "../../Util/common";
import { translateEnglishToSpanish, translateFromSpanish } from "../../Util/translator";
import ScofaAppColumn from "../../Components/ScofaApp/ScofaAppColumn";
import { SCOFA_TABLE_HEADER_KEY } from "../../Components/ScofaApp/ScofaAppData";
import ReadOnlyDropdown from "../../Components/ReadOnlyDropdown";

const MeditationManagement = () => {

  const scofaColumnRef = useRef();
  const { permissions } = useContext(PermissionsContext)

  const [data, setData] = useState([]);
  const [isListPositionUpdated, setIsListPositionUpdated] = useState(false);
  const sortRef = useRef(null);

  const [enteries, setEnteries] = useState(10);
  const [offset, setOffset] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterSearchValue, setFilterSearchValue] = useState("");
  const [listDropdownValue, setListDropdownValue] = useState("all");
  const [sort, setSort] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [swapId, setSwapId] = useState(null);
  const [swapRecordId, setSwapRecordId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState({ error: true, text: '' });
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("english");

  useEffect(() => {
    if (currentPage != undefined) {
      setOffset(currentPage);
    }
  }, [currentPage])

  useEffect(() => {
    document.title = "Relax Scofa | Relax & Rest Management";
  }, []);

  useEffect(() => {
    fetchData();
  }, [enteries, filterSearchValue, dateTo, offset, listDropdownValue, selectedLanguage]);

  const fetchData = async () => {
    try {
      const languageParam = selectedLanguage === "spanish" ? "&language=spanish" : "&language=english";
      const response = await axios.get(
        `${BASEURL}/api/meditation/?limit=${enteries}&offset=${offset}&start_date=${dateFrom}&end_date=${dateTo}&search=${filterSearchValue}&featured=${listDropdownValue == 'featured' ? true : ''}${languageParam}`
      );
      if (response.data.error != true) {
        setData(response.data.data);
        setTotalCount(response.data.count);
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const deleteMeditation = async (id) => {
    try {
      const response = await deleteRequest(`/api/meditation/${id}`);
      setData(data.filter((item) => item.id !== id));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteMeditationAction = (id, name) => {
    questionModal
      .fire({
        title: `Do you want to delete ${name} Relax & Rest?`,
        confirmButtonText: "Yes",
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteMeditation(id);
          successModal.fire({
            text: `${name} Relax & Rest has been deleted`,
            confirmButtonText: "Continue",
          });
        }
      });
  };

  const handleDuplicateMeditation = async (item) => {
    // Determine target language
    const currentLanguage = item.language || 'english';
    const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';
    const targetLanguageText = targetLanguage === 'english' ? 'English' : 'Spanish';

    questionModal
      .fire({
        title: `Do you want to duplicate this Relax & Rest into ${targetLanguageText.toLowerCase()}?`,
        confirmButtonText: `Duplicate into ${targetLanguageText}`,
      })
      .then((result) => {
        if (result.isConfirmed) {
          duplicateMeditation(item);
        }
      });
  };

  const duplicateMeditation = async (item) => {
    setLoader(true);
    try {
      // Fetch full meditation data
      const response = await axios.get(`${BASEURL}/api/meditation/${item.id}`);
      const meditationData = response.data.data;
      if (!meditationData) {
        setLoader(false);
        return;
      }

      // Determine current language and target language
      const currentLanguage = meditationData.language || 'english';
      const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';

      // Translate title
      let translatedTitle = meditationData.title;
      if (meditationData.title) {
        let translationResult;
        if (currentLanguage === 'english') {
          translationResult = await translateEnglishToSpanish(meditationData.title);
        } else {
          translationResult = await translateFromSpanish(meditationData.title);
        }

        if (translationResult.success) {
          translatedTitle = translationResult.translatedText;
        } else {
          setLoader(false);
          successModal.fire({
            text: `Translation failed: ${translationResult.error}`,
            confirmButtonText: "Continue",
          });
          return;
        }
      }

      // Create FormData for new meditation
      const formDataToSend = new FormData();

      // // Add image and thumbnail if available
      // if (meditationData.image) {
      //   try {
      //     const imageResponse = await fetch(meditationData.image);
      //     const imageBlob = await imageResponse.blob();
      //     formDataToSend.append("image", imageBlob, "image.jpg");
      //   } catch (error) {
      //     console.log("Could not fetch image:", error);
      //   }
      // }

      // if (meditationData.thumbnail) {
      //   try {
      //     const thumbnailResponse = await fetch(meditationData.thumbnail);
      //     const thumbnailBlob = await thumbnailResponse.blob();
      //     formDataToSend.append("thumbnail", thumbnailBlob, "thumbnail.jpg");
      //   } catch (error) {
      //     console.log("Could not fetch thumbnail:", error);
      //   }
      // }

      // Add other fields
      formDataToSend.append("image", meditationData.image);
      formDataToSend.append("thumbnail", meditationData.thumbnail);

      formDataToSend.append("title", translatedTitle);
      formDataToSend.append("premium", meditationData.premium || false);
      formDataToSend.append("audio", meditationData.audio);
      if (meditationData?.video_url) {
        formDataToSend.append("video_url", meditationData.video_url);
      }
      if (meditationData?.video_mobile_url) {
        formDataToSend.append("video_mobile_url", meditationData.video_mobile_url);
      }

      // Add category
      if (meditationData.subcategory_obj?.child_subcategory_id) {
        formDataToSend.append("meditationcategory", meditationData.subcategory_obj.child_subcategory_id);
      } else if (meditationData.meditationcategory) {
        formDataToSend.append("meditationcategory", meditationData.meditationcategory);
      }

      formDataToSend.append("featured", meditationData.featured || false);
      formDataToSend.append("child_content", meditationData.child_content || false);
      formDataToSend.append("language", targetLanguage);
      formDataToSend.append("duration", meditationData?.duration);

      // Create the duplicate meditation
      const createResponse = await postRequest(`/api/meditation/`, formDataToSend, null);

      if (createResponse.data.error === false) {
        successModal.fire({
          text: `Relax & Rest duplicated successfully in ${targetLanguage}!`,
          confirmButtonText: "Continue",
        });
        fetchData();
        setLoader(false);
      } else {
        setLoader(false);
        successModal.fire({
          text: `Failed to duplicate Relax & Rest: ${createResponse.data.message || 'Unknown error'}`,
          confirmButtonText: "Continue",
        });
      }
    } catch (error) {
      console.log("error =======>   ", error);
      setLoader(false);
      successModal.fire({
        text: `An error occurred while duplicating Relax & Rest: ${error.message || 'Unknown error'}`,
        confirmButtonText: "Continue",
      });
    }
  };

  const meditationHeader = [
    SCOFA_TABLE_HEADER_KEY,
    {
      key: "id",
      title: "S.No",
    },
    {
      key: "title",
      title: "Title",
    },
    {
      key: "type",
      title: "Type",
    },
    {
      key: "category",
      title: "Category",
    },
    {
      key: "subcategory",
      title: "Sub Category",
    },
    {
      key: "underagecontent",
      title: "Under age content",
    },
    {
      key: "actions",
      title: "Actions",
    },
  ];

  const sortingValues = [
    {
      value: "all",
      text: "All",
    },
    {
      value: "status",
      text: "Status",
    },
    {
      value: "registered",
      text: "Registered Date",
    },
  ];


  const swipModal = (id, recordId) => {
    setError({ error: false, text: '' })
    setSwapId(id);
    // setSwapRecordId(recordId);
    setShowModal(true);
  }

  const setSorting = async () => {
    setError({ error: false, text: '' });
    let recordId = parseInt(sortRef.current.value, 10);
    if (recordId != '') {
      if (sortRef.current.value < data.length && sortRef.current.value > 0) {
        console.log(data[swapId - 1].id, data[recordId - 1].id);
        setShowModal(false);
        const formDataToSend = new FormData();
        formDataToSend.append("meditation_id_1", data[swapId - 1].id);
        formDataToSend.append("meditation_id_2", data[recordId - 1].id);
        try {
          const response = await axios.put(`${BASEURL}/api/meditation/swap-meditations/`, formDataToSend);
          setLoader(true);
          async function fetchData() {
            try {
              const response = await axios.get(
                `${BASEURL}/api/meditation/?limit=${enteries}&offset=${offset}&start_date=${dateFrom}&end_date=${dateTo}&search=${filterSearchValue}`
              );
              if (response.data.error != true) {
                setData(response.data.data);
                setTotalCount(response.data.count);
                setLoader(false);
              } else {
                console.log(response.data.message);
              }
            } catch (error) {
              console.error(error);
            }
          }
          fetchData();
        } catch (error) {
          console.log(error);
        }
      } else {
        setError({ error: true, text: 'Please Enter Correct No' });
      }
    } else {
      setError({ error: true, text: 'Please Enter No' });
    }
  }

  const modulesWisePermission = (condition) => {
    if (!permissions) {
      return 'd-none'
    }
    return disabledButtonByPermission(permissions, condition)
  }

  const handleChangeEntries = (no) => {
    setOffset(0)
    setEnteries(no)
  }

  const onSortEnd = (result) => {
    if (!result.destination) return;

    const oldIndex = result.source.index;
    const newIndex = result.destination.index;

    if (oldIndex === newIndex) return;

    const reorderedList = Array.from(data);
    const [movedItem] = reorderedList.splice(oldIndex, 1);
    reorderedList.splice(newIndex, 0, movedItem);
    setIsListPositionUpdated(true);
    setData(reorderedList)

  };

  const updateOrdering = async () => {

    try {
      questionModal
        .fire({
          title: getQuestionModalTitleForSorting(listDropdownValue, 'RelaxRest'),
          confirmButtonText: "Yes",
        })
        .then((result) => {
          if (result.isConfirmed) {
            setLoader(true);
            callSortingApi()
          }
        });
    } catch (error) {
      console.log(error)
    }

  }

  const callSortingApi = async () => {
    try {
      const url = listDropdownValue === 'all' ? '/api/meditation/update-sequence/' : `/api/meditation/update-featured-sequence/`;
      const result = await listSorted({
        url: url,
        data: data,
        key: 'meditationid'
      })
      if (!result.data.error) {
        setLoader(false);
        successModal.fire({
          text: sortingUpdatedSuccessfullyMsg(listDropdownValue, 'RelaxRest'),
          confirmButtonText: "Continue",
        });
        setIsListPositionUpdated(false);
      } else {
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  }

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-lg-4 mb-2">
              <h2 className="mainTitle">Relax & Rest </h2>
            </div>
            <div className="col-lg-8 text-end mb-2">
              <button onClick={() => scofaColumnRef.current.handleScofaFlag()} className="customButton primaryButton me-3">
                Update Scofa Tracks
              </button>
              <button disabled={data.length > 0 && isListPositionUpdated ? false : true} className="customButton primaryButton me-3" onClick={updateOrdering}>
                Update Ordering
              </button>
              <Link
                to={"/relax-rest-management/add-relax-rest"}
                className={`customButton primaryButton ${modulesWisePermission('can_add')}`}
              >
                Add Relax & Rest
              </Link>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CustomFilters
                enteries={enteries}
                totalCount={totalCount}
                entriesFilter={true}
                setEnteries={handleChangeEntries}
                filterSearch={true}
                filterSearchValue={filterSearchValue}
                setFilterSearchValue={setFilterSearchValue}
                listDropdown={true}
                listDropdownValue={listDropdownValue}
                setListDropdownValue={setListDropdownValue}
                // dateFilter={true}
                setDateFrom={setDateFrom}
                setDateTo={setDateTo}
                languageFilter
                languageValue={selectedLanguage}
                setLanguageValue={setSelectedLanguage}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <DragDropContext onDragEnd={onSortEnd}>
                <Droppable droppableId="soundMix-list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <CustomTable headers={meditationHeader}>
                        <tbody>
                          {data && data.map((item, index) => (
                            <Draggable
                              draggableId={`mix-${item.id}`}
                              index={index}
                              key={item.id}
                            >
                              {(provided, snapshot) => (
                                <tr ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`my-1 ${snapshot.isDragging ? "dragging" : ""}`}
                                >
                                  <ScofaAppColumn ref={scofaColumnRef} item={item} category="meditation" />
                                  <td>
                                    {/* <button type="button" className="btn lightColor outline-0 border-0" onClick={() => swipModal(index + 1, item.id)}>{index + 1}</button> */}
                                    {index + 1}
                                  </td>
                                  <td className="text-capitalize">
                                    <img
                                      src={
                                        item.thumbnail
                                          ? `${item.thumbnail}`
                                          : placeholderImage
                                      }
                                      alt="thumbnail"
                                      className="thumbnail"
                                    />
                                    {item.title}
                                  </td>
                                  <td>{item.premium ? "Premium" : "Free"}</td>
                                  <td><ReadOnlyDropdown options={item?.categories} /></td>
                                  <td><ReadOnlyDropdown options={item?.subcategories} /></td>
                                  <td>{item?.child_content ? "YES" : "NO"}</td>
                                  <td>
                                    <Dropdown className="tableDropdown">
                                      <Dropdown.Toggle
                                        variant="transparent"
                                        className="notButton classicToggle"
                                      >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu
                                        align="end"
                                        className="tableDropdownMenu"
                                      >
                                        <Link
                                          to={`/relax-rest-management/relax-rest-details/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_view')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEye}
                                            className="tableActionIcon"
                                          />
                                          View
                                        </Link>
                                        <Link
                                          to={`/relax-rest-management/edit-relax-rest/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_edit')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEdit}
                                            className="tableActionIcon"
                                          />
                                          Edit
                                        </Link>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_add')}`}
                                          onClick={() => {
                                            handleDuplicateMeditation(item);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faCopy}
                                            className="tableActionIcon"
                                          />
                                          Duplicate
                                        </button>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_delete')}`}
                                          onClick={() => {
                                            deleteMeditationAction(item.id, item.title);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faTimes}
                                            className="tableActionIcon"
                                          />
                                          Delete
                                        </button>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </td>
                                </tr>
                              )}
                            </Draggable>

                          ))}
                          {provided.placeholder}
                        </tbody>
                      </CustomTable>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CustomPagination
                enteries={enteries}
                totalCount={totalCount}
                setCurrentPage={setOffset}
                currentPage={offset}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>

      {loader && <CustomLoader />}

      <Modal
        show={showModal}
        centered
        className="swap-modal"
        backdrop="static"
      >
        <Modal.Body>
          <div className="pb-5 w-100 d-flex flex-column align-items-center">
            <div className="text-end ms-auto">
              <button
                className="closeButton notButton ms-auto"
                onClick={() => { setShowModal(false); }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cross-icon"
                  color="white"
                />
              </button>
            </div>
            <div className="w-100 d-flex flex-column align-items-center" style={{ maxWidth: '500px' }}>
              <div className="d-flex align-items-center mt-3 pt-3">
                <h2 className="mb-0 me-4">Record <b className="mx-2">{swapId}</b> swap with </h2>
                <input type="number" ref={sortRef} onChange={() => setError({ error: false, text: '' })} />
              </div>
              {error?.error && <small className="text-danger mt-3">{error.text}</small>}
            </div>
            <div className="mt-3 pt-3 d-flex gap-3 justify-content-center">
              <button style={{ backgroundColor: '#ffffff1f' }} className='px-5 py-2 fs-6 fw-light text-white text-capitalize btn rounded-pill d-flex align-items-center justify-content-center flex-shrink-0' onClick={setSorting}>
                <span className=''>Update</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </>
  );
};
export default MeditationManagement;
